import { GLYPH_RENDERERS, type IconName } from "@secops/icons";

// Primary-nav glyphs for the collapsible icon rail.
//
// Geometry now comes from @secops/icons, the package generated from the Design-locked
// sprite board (icon coverage expansion, 2026-08-15), so the shipped rail and the
// approved drawing cannot drift and a new nav entry never means hand-copying path data.
// The stroke language is unchanged: outline paths only, `fill: none`, `stroke:
// currentColor` supplied by the `.sidenav__icon` CSS, so every glyph inherits the link's
// own color and stays legible in both themes, at both brand hues, and in forced-colors
// mode. No emoji, no icon font, no CDN - a nav that is icons-only when collapsed cannot
// depend on a remote asset to be usable.
//
// One glyph per nav key. `NAV_ICON_KEYS` is the closed set, and nav-model.test pins that
// every NavItem has one, so a new nav entry cannot ship as a blank square. The
// `satisfies` clause pins every key to a real sprite id at compile time, so a typo'd or
// renamed glyph is a type error, not a blank square at runtime.

export const NAV_ICON_KEYS = [
  "inbox",
  "command",
  "patrols",
  "schedule",
  "setup",
  "incidents",
  "reports",
  "admin",
  "platform",
  "help",
] as const satisfies readonly IconName[];

export type NavIconKey = (typeof NAV_ICON_KEYS)[number];

/**
 * Decorative by contract: `aria-hidden` because the link's own text (or its aria-label when
 * the rail is collapsed) is the accessible name. An icon that also announced itself would
 * make every item read twice.
 */
export function NavIcon({ icon }: { icon: NavIconKey }) {
  return (
    <svg className="sidenav__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {GLYPH_RENDERERS[icon]()}
    </svg>
  );
}

/** Chevron for the collapse control. CSS rotates it 180° in the collapsed state. */
export function NavCollapseIcon() {
  return (
    <svg className="sidenav__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m13.5 7-5 5 5 5M19 7l-5 5 5 5" transform="translate(-1.5 0)" />
    </svg>
  );
}
